import { createSelectorFunctions } from 'auto-zustand-selectors-hook';
import { isNil, omit } from 'es-toolkit';
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';

export interface FavoritesState {
  favorites: {
    [guideId: string]: {
      registeredButtonCount: number;
      isFavorited: boolean;
      isLocked: boolean;
    };
  };
  favoriteOnAuthCallback: (() => void) | null;
}

export interface FavoritesActions {
  registerButton: (guideId: string, initialIsFavorited?: boolean) => void;
  unregisterButton: (guideId: string) => void;
  setFavoriteOnAuthCallback: (callback: FavoritesState['favoriteOnAuthCallback']) => void;
  toggle: (guideId: string, isFavoritedOverride?: boolean) => void;
  unlock: (guideId: string) => void;
}

export type FavoritesStore = FavoritesState & FavoritesActions;
export type InitialFavoritesState = Partial<FavoritesState>;

const defaultState: FavoritesState = {
  favorites: {},
  favoriteOnAuthCallback: null,
};

export function createFavoritesStore(initialState: InitialFavoritesState = {}) {
  const favoritesStore = create<FavoritesStore>()(
    immer((set, get) => {
      return {
        ...defaultState,
        ...initialState,

        registerButton: (guideId, initialIsFavorited) => {
          set((state) => {
            const guideIdFavoriteState = state.favorites[guideId];

            if (!guideIdFavoriteState) {
              state.favorites[guideId] = {
                registeredButtonCount: 1,
                isFavorited: !!initialIsFavorited,
                isLocked: false,
              };
              return;
            }

            state.favorites[guideId] = {
              ...guideIdFavoriteState,
              registeredButtonCount: guideIdFavoriteState.registeredButtonCount + 1,
              // Registration can happen before async account favorite data settles after auth changes. Let the latest
              // guide metadata refresh the shared state for now. Longer term, registrations should have their own ids
              // so stale buttons can unregister their own state instead of sharing one guide-id-level state.
              isFavorited: !!initialIsFavorited,
            };
          });
        },
        unregisterButton: (guideId) => {
          set((state) => {
            const guideIdFavoriteState = state.favorites[guideId];

            if (!(guideIdFavoriteState.registeredButtonCount - 1)) {
              state.favorites = omit(state.favorites, [guideId]);
              return;
            }

            state.favorites[guideId] = {
              ...guideIdFavoriteState,
              registeredButtonCount: guideIdFavoriteState.registeredButtonCount - 1,
            };
          });
        },
        toggle: (guideId, isFavoritedOverride) =>
          set((state) => {
            const guideIdFavoriteState = state.favorites[guideId];

            state.favorites[guideId] = {
              ...guideIdFavoriteState,
              isFavorited: isFavoritedOverride ?? !guideIdFavoriteState.isFavorited,
              isLocked: isNil(isFavoritedOverride), // no need to lock when performing an override
            };
          }),
        unlock: (guideId) =>
          set((state) => {
            const guideIdFavoriteState = state.favorites[guideId];

            state.favorites[guideId] = {
              ...guideIdFavoriteState,
              isLocked: false,
            };
          }),
        setFavoriteOnAuthCallback: (callback) =>
          set((state) => {
            if (!callback) {
              state.favoriteOnAuthCallback = null;
              return;
            }

            state.favoriteOnAuthCallback = () => {
              callback();
              // Clear self after execution. The callback is only meant to be called once.
              get().setFavoriteOnAuthCallback(null);
            };
          }),
      };
    }),
  );

  return createSelectorFunctions(favoritesStore);
}
